import {
  emailMessage,
  invalidMessage
} from 'messages'
import { TRAINING_CLASS_NAMES } from 'models/constants/training-class-names'
import { ARTICLE_MODEL_CLASS_NAME } from 'models/daos/article/constants'
import { PAGE_MODEL_CLASS_NAME } from 'models/daos/page/constants'
import {
  USER_MODEL_CLASS_NAME,
  USER_MODEL_KEY,
  USER_MODEL_REGEX,
  USER_MODEL_ROLE,
  USER_MODEL_VALIDATOR_MIN_AGE
} from 'models/daos/user/constants'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { isInLengthRangeValidatorUtil } from 'modules/redux/validators/legacy'
import { getAgeDatetimeUtil } from 'utils/datetime'
import { splitAndTrimAll } from 'utils/string'
import { isEmailValidatorUtil } from 'utils/validators/is-email'
import { getObjectUtil } from 'packages/src'

const VALIDATOR = {
  birthday: birthdayUserUtil,
  email: emailUserUtil,
  name: nameUserUtil
}

function getValidatorUserUtil (key) {
  return VALIDATOR[key]
}

function birthdayUserUtil (value) {
  const date = new Date(value)
  if (!date || !date.getTime()) {
    return invalidMessage('birthday')
  }
  const age = getAgeDatetimeUtil(date)
  if (age < USER_MODEL_VALIDATOR_MIN_AGE) {
    return `Looks like you're too young, make sure to be ${USER_MODEL_VALIDATOR_MIN_AGE} years old minimum.`
  }
  return true
}

function emailUserUtil (value) {
  if (!isEmailValidatorUtil(value)) {
    return emailMessage()
  }
  return true
}

function nameUserUtil (value, className) {
  const validate = isInLengthRangeValidatorUtil('name', value, className)
  if (validate !== true) {
    return validate
  }
  if (!USER_MODEL_REGEX[USER_MODEL_KEY.NAME].test(value)) {
    return 'This name should only contain alphanumeric characters (letters A-Z, numbers 0-9) with the exception of underscores, dashes and periods.'
  }
  return true
}

/**
 * Returns true if the user is an admin.
 * @param  {Object}  user
 * @returns {boolean}
 */
function getRoleUserUtil (user) {
  return (
    getObjectUtil(user, 'data.role') ||
    getObjectUtil(user, 'role')
  )
}

/**
 * Returns true if the user is an admin.
 * @param  {Object}  user
 * @returns {boolean}
 */
function isAdminUserUtil (user) {
  const role = getRoleUserUtil(user)
  return USER_MODEL_ROLE.ADMIN === role
}

/**
 * A mapping of class name to array of user roles.
 * @type {Object}
 */
const MODEL_ROLE = {
  [ARTICLE_MODEL_CLASS_NAME]: [USER_MODEL_ROLE.WRITER],
  [PAGE_MODEL_CLASS_NAME]: [USER_MODEL_ROLE.WRITER]
}

/**
 * Whether or not the current User can edit the instance associated to the given
 * `author`.
 * @param  {User}  currentUser
 * @param  {Instance|String}  instanceOrClassName
 * @returns {boolean}
 */
function isEditableUserUtil (currentUser, instanceOrClassName) {
  let className
  let instance
  if (!currentUser || !instanceOrClassName) {
    return false
  }
  if (typeof instanceOrClassName === 'string') {
    className = instanceOrClassName
  } else {
    instance = instanceOrClassName
    className = instanceOrClassName.className
  }
  // TODO update once auth/authz is figured it out
  if (TRAINING_CLASS_NAMES.includes(className)) {
    return true
  }
  // If it's a User, check if it's the current User
  if (className === USER_MODEL_CLASS_NAME) {
    const instanceId = getObjectUtil(instance, 'data.id')
    const currentUserId = getObjectUtil(currentUser, 'data.id')
    return (
      (
        instanceId &&
        instanceId === currentUserId
      ) ||
      isAdminUserUtil(currentUser)
    )
  }
  const classNameToRoles = MODEL_ROLE[className]
  return (
    (
      classNameToRoles &&
      classNameToRoles.includes(currentUser.data.role)
    ) ||
    isAdminUserUtil(currentUser)
  )
}

/**
 * Checks whether the given User can create instances.
 * @param  {User} user
 * @returns {boolean}
 */
function canCreateInstancesUserUtil (user) {
  return !!getObjectUtil(user, 'data.role')
}

/**
 * Gets the user's email address.
 * @param  {User} user
 * @returns {string}
 */
function getEmailUserUtil (user) {
  return (
    user.data.email ||
    user.data.facebookEmail ||
    user.data.twitterEmail ||
    user.data.googleEmail
  )
}

/**
 * Returns the city name where the user is probably in.
 * e.g. 'San Antonio, TX, US' => 'San Antonio'
 *
 * @param  {User} user
 * @returns {string}
 */
function getCityUserUtil (user) {
  const { location } = user.data
  if (!location) {
    return null
  }
  return splitAndTrimAll(location, ',')[0]
}

/**
 * Returns the city name where the user is probably in.
 * e.g. 'San Antonio, TX, US' => 'TX'
 * @param  {User} user
 * @returns {string}
 */
function getStateUserUtil (user) {
  const { location } = user.data
  if (!location) {
    return null
  }
  return splitAndTrimAll(location, ',')[1]
}

/**
 * Gets the user's full name.
 * @param  {Instance} user
 * @returns {string}
 */
function getFullNameUserUtil (user) {
  const firstName = getValueReduxUtil(user, 'firstName')
  const lastName = getValueReduxUtil(user, 'lastName')
  return lastName ? `${firstName} ${lastName}` : firstName
}

function getFirstNameFromFullNameUserUtil (fullName) {
  return fullName.split(' ')[0]
}

function getLastNameFromFullNameUserUtil (fullName) {
  const lastNameArr = fullName.split(' ').slice(1)
  const lastName = lastNameArr.join(' ')
  return lastNameArr.length === 1 && !lastName ? ' ' : lastName
}

function getWebsiteUserUtil (value) {
  if (value.includes('http')) {
    return value
  }
  return `http://${value}`
}

export {
  getValidatorUserUtil,
  isAdminUserUtil,
  isEditableUserUtil,
  canCreateInstancesUserUtil,
  getEmailUserUtil,
  getCityUserUtil,
  getStateUserUtil,
  getFullNameUserUtil,
  getFirstNameFromFullNameUserUtil,
  getLastNameFromFullNameUserUtil,
  getWebsiteUserUtil
}
